import React, { useState, useRef, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import EditJobModal from "./EditJobModal";
import { formatDateLocalEnhanced } from "../../utils/dateUtils";
import { hasAnyEditPermission } from "./utils/schedulePermissions";
import { getProjectId, getProjectStatus } from "../../utils/dataNormalization";
import SortableTableHeader from "./SortableTableHeader";
import { getNextSortConfig, sortTableRows } from "./utils/tableSorting";

const TABLE_COLUMNS = [
  { key: "JobNumber", label: "Job Number", type: "text" },
  { key: "ProjectName", label: "Project Name", type: "text" },
  { key: "ModelNumber", label: "Model Number", type: "text" },
  { key: "JobStatus", label: "Job Status", type: "text" },
  { key: "ProjectStatus", label: "Project Status", type: "text", getValue: getProjectStatus },
  { key: "ProjectManager", label: "Project Manager", type: "text" },
  { key: "OrderDate", label: "Order Date", type: "date" },
  { key: "ProductionRelease", label: "Release To Scheduling", type: "date" },
  { key: "SalesRep", label: "Sales Rep", type: "text" },
];

const FullReleaseStatusMismatch = ({ filteredJobs = null, canEdit = true }) => {
  const jobScheduleDataFromRedux = useSelector((state) => state.jobSchedule.data);
  const jobScheduleData = filteredJobs || jobScheduleDataFromRedux;
  const userRoles = useSelector((state) => state.user?.userRoles) || [];
  const hasEditAccess = Boolean(canEdit) && hasAnyEditPermission(userRoles);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [visibleRows, setVisibleRows] = useState(50);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const containerRef = useRef(null);

  const handleRowDoubleClick = (job) => {
    if (hasEditAccess) {
      setSelectedJob(job);
      setShowScheduleModal(true);
    }
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setSelectedJob(null);
  };

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        setVisibleRows((prev) => prev + 50);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const fullReleaseMismatchJobs = useMemo(
    () => Array.isArray(jobScheduleData)
      ? jobScheduleData.filter((job) => {
          const jobStatus = job?.JobStatus?.trim();
          const projectStatus = getProjectStatus(job)?.trim();

          return jobStatus === "Full Release" && projectStatus !== "Full Release";
        })
      : [],
    [jobScheduleData]
  );

  const sortedMismatchJobs = useMemo(
    () => sortTableRows(fullReleaseMismatchJobs, TABLE_COLUMNS, sortConfig),
    [fullReleaseMismatchJobs, sortConfig]
  );
  const visibleMismatchJobs = sortedMismatchJobs.slice(0, visibleRows);

  const handleSort = (key) => {
    setSortConfig((currentSort) => getNextSortConfig(currentSort, key));
    setVisibleRows(50);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const formatDate = (dateString) => formatDateLocalEnhanced(dateString);

  return (
    <div
      className="full-release-status-mismatch-container"
      style={{ overflow: "auto", maxHeight: "80vh" }}
      ref={containerRef}
    >
      <h4>Full Release Status Mismatch ({fullReleaseMismatchJobs.length})</h4>

      <Table striped bordered hover responsive className="schedule-responsive-table">
        <SortableTableHeader
          columns={TABLE_COLUMNS}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        <tbody>
          {visibleMismatchJobs.length > 0 ? (
            visibleMismatchJobs.map((job, index) => (
              <tr
                key={`${getProjectId(job) || job?.JobNumber || "job"}-${index}`}
                onDoubleClick={() => handleRowDoubleClick(job)}
                style={{ cursor: hasEditAccess ? "pointer" : "default" }}
                title={hasEditAccess ? "Double-click to edit" : ""}
              >
                <td data-label="Job Number">{job.JobNumber}</td>
                <td data-label="Project Name">{job.ProjectName}</td>
                <td data-label="Model Number">{job.ModelNumber}</td>
                <td data-label="Schedule Status">{job.JobStatus}</td>
                <td data-label="Portal Status">{getProjectStatus(job)}</td>
                <td data-label="Project Manager">{job.ProjectManager}</td>
                <td data-label="Order Date">{formatDate(job.OrderDate)}</td>
                <td data-label="Production Release">{formatDate(job.ProductionRelease)}</td>
                <td data-label="Sales Rep">{job.SalesRep}</td>
                {hasEditAccess && (
                  <td data-label="Actions" className="schedule-card-action-cell">
                    <button type="button" className="btn btn-primary schedule-card-action" onClick={() => handleRowDoubleClick(job)}>
                      Open job
                    </button>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="schedule-empty-row">No full release status mismatch jobs</td>
            </tr>
          )}
        </tbody>
      </Table>

      {visibleRows < sortedMismatchJobs.length && (
        <div style={{ textAlign: "center", padding: "10px" }}>Loading more rows...</div>
      )}

      <Modal
        className="job-schedule-modal"
        show={showScheduleModal}
        onHide={() => {
          const modalComponent = document.querySelector(".modal-content")?.closest(".modal");
          const closeButton = modalComponent?.querySelector(".modal-header .btn-close");

          if (closeButton) {
            closeButton.click();
          } else {
            handleCloseScheduleModal();
          }
        }}
        size="xl"
      >
        <EditJobModal
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          handleCloseModal={handleCloseScheduleModal}
          handleSaveChanges={() => {}}
          canEdit={hasEditAccess}
        />
      </Modal>
    </div>
  );
};

export default FullReleaseStatusMismatch;
